import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPortfolioPresets, getPortfolioLocations, createPortfolioLocation, deletePortfolioLocation,
  searchPortfolioAsset, getPortfolioFxRate, createPortfolioTransaction, getPortfolioTransactions,
  getPortfolioCashflows, createPortfolioCashflow, deletePortfolioCashflow, getPortfolioSummary, getPortfolioAssetStatus,
  getPortfolioTimeline, getTechnicalHistory,
} from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}
function fmtPct(n) {
  return n == null || !Number.isFinite(n) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}
function fmtQty(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
}
function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

export default function PortfolioPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('holdings'); // 'holdings' | 'transactions' | 'cashflow' | 'locations'

  const [presets, setPresets] = useState({ cexPresets: [], walletAppPresets: [], walletNetworkPresets: [] });
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [cashflows, setCashflows] = useState([]);
  const [ready, setReady] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState(null);

  const [showAddTx, setShowAddTx] = useState(false);
  const [showAddCashflow, setShowAddCashflow] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);

  const load = useCallback(async () => {
    setReady(false);
    try {
      const [pr, loc, sum, tx, cf] = await Promise.all([
        getPortfolioPresets(), getPortfolioLocations(), getPortfolioSummary(),
        getPortfolioTransactions(), getPortfolioCashflows(),
      ]);
      setPresets(pr);
      setLocations(loc.locations || []);
      setSummary(sum);
      setTransactions(tx.transactions || []);
      setCashflows(cf.cashflows || []);
    } catch { /* silent — sections render their own empty states */ }
    finally { setReady(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;
  const totals = summary?.totals;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <h2 style={s.title}>💼 {t('portfolio.title')}</h2>
        <p style={s.subtitle}>{t('portfolio.subtitle')}</p>
      </div>

      <div style={s.notice}>{t('portfolio.notice')}</div>

      <WorkflowGuide t={t} />

      {ready && totals && (
        <div style={s.cards}>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.totalValue')}</span>
            <span style={s.cardValue}>{fmtUsd(totals.totalCurrentValueUsd)}</span>
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.unrealizedPnl')}</span>
            <span style={{ ...s.cardValue, color: totals.totalUnrealizedPnlUsd >= 0 ? '#10b981' : '#ef4444' }}>
              {fmtUsd(totals.totalUnrealizedPnlUsd)}
            </span>
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.realizedPnl')}</span>
            <span style={{ ...s.cardValue, color: totals.totalRealizedPnlUsd >= 0 ? '#10b981' : '#ef4444' }}>
              {fmtUsd(totals.totalRealizedPnlUsd)}
            </span>
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.netPnlOnCapital')}</span>
            <span style={{ ...s.cardValue, color: totals.netPnlOnCapitalUsd >= 0 ? '#10b981' : '#ef4444' }}>
              {fmtUsd(totals.netPnlOnCapitalUsd)}
            </span>
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.totalFeesTax')}</span>
            <span style={s.cardValue}>{fmtUsd(totals.totalFeesUsd + totals.totalTaxUsd)}</span>
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.cashBalance')}</span>
            <span style={{ ...s.cardValue, color: totals.cashBalanceUsd < 0 ? '#ef4444' : 'var(--text)' }}>
              {fmtUsd(totals.cashBalanceUsd)}
            </span>
            {totals.cashBalanceUsd < 0 && (
              <span style={{ fontSize: 10.5, color: '#ef4444', lineHeight: 1.4 }}>{t('portfolio.cashBalanceNegativeHint')}</span>
            )}
          </div>
          <div style={s.card}>
            <span style={s.cardLabel}>{t('portfolio.depositedWithdrawn')}</span>
            <span style={s.cardValue}>
              <span style={{ color: '#10b981' }}>{fmtUsd(totals.totalDepositedUsd)}</span>
              {' / '}
              <span style={{ color: '#ef4444' }}>{fmtUsd(totals.totalWithdrawnUsd)}</span>
            </span>
          </div>
        </div>
      )}

      <div style={s.tabBar}>
        {[['holdings', t('portfolio.tabHoldings')], ['transactions', t('portfolio.tabTransactions')], ['cashflow', t('portfolio.tabCashflow')], ['timeline', t('portfolio.tabTimeline')], ['locations', t('portfolio.tabLocations')]].map(([v, label]) => (
          <button key={v} type="button" style={tab === v ? s.tabBtnActive : s.tabBtn} onClick={() => setTab(v)}>{label}</button>
        ))}
      </div>

      {!ready ? (
        <div style={s.loading}>{t('portfolio.loading')}</div>
      ) : tab === 'holdings' ? (
        <HoldingsTab
          holdings={summary?.holdings || []}
          expandedSymbol={expandedSymbol}
          setExpandedSymbol={setExpandedSymbol}
          onAddTx={() => setShowAddTx(true)}
          t={t}
        />
      ) : tab === 'transactions' ? (
        <TransactionsTab
          transactions={transactions}
          onAdd={() => setShowAddTx(true)}
          t={t}
        />
      ) : tab === 'cashflow' ? (
        <CashflowTab
          cashflows={cashflows}
          onAdd={() => setShowAddCashflow(true)}
          onDelete={async (id) => { await deletePortfolioCashflow(id); load(); }}
          t={t}
        />
      ) : tab === 'timeline' ? (
        <TimelineTab t={t} isMobile={isMobile} />
      ) : (
        <LocationsTab
          locations={locations}
          presets={presets}
          onAdd={() => setShowAddLocation(true)}
          onDelete={async (id) => { try { await deletePortfolioLocation(id); load(); } catch (e) { alert(e.message); } }}
          t={t}
        />
      )}

      {showAddTx && (
        <AddTransactionModal
          locations={locations}
          presets={presets}
          holdings={summary?.holdings || []}
          cashBalanceUsd={totals?.cashBalanceUsd ?? 0}
          onClose={() => setShowAddTx(false)}
          onSaved={() => { setShowAddTx(false); load(); }}
          t={t}
        />
      )}
      {showAddCashflow && (
        <AddCashflowModal
          cashBalanceUsd={totals?.cashBalanceUsd ?? 0}
          onClose={() => setShowAddCashflow(false)}
          onSaved={() => { setShowAddCashflow(false); load(); }}
          t={t}
        />
      )}
      {showAddLocation && (
        <AddLocationModal
          presets={presets}
          onClose={() => setShowAddLocation(false)}
          onSaved={() => { setShowAddLocation(false); load(); }}
          t={t}
        />
      )}
    </div>
  );
}

// ── Holdings ────────────────────────────────────────────────────────────
function HoldingsTab({ holdings, expandedSymbol, setExpandedSymbol, onAddTx, t }) {
  if (holdings.length === 0) {
    return (
      <div style={s.empty}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💼</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('portfolio.noHoldings')}</div>
        <button type="button" style={{ ...s.primaryBtn, alignSelf: 'center' }} onClick={onAddTx}>+ {t('portfolio.addTransaction')}</button>
      </div>
    );
  }
  return (
    <div style={s.holdingsList}>
      <button type="button" style={{ ...s.primaryBtn, marginBottom: 12 }} onClick={onAddTx}>+ {t('portfolio.addTransaction')}</button>
      {holdings.map((h) => (
        <div key={h.symbol} style={s.holdingCard}>
          <button type="button" style={s.holdingHead} onClick={() => setExpandedSymbol(expandedSymbol === h.symbol ? null : h.symbol)}>
            <span style={s.holdingSymbol}>{expandedSymbol === h.symbol ? '▾' : '▸'} {h.symbol}</span>
            <span style={s.holdingQty}>{fmtQty(h.openQuantity)}</span>
            <span style={s.holdingPrice}>{fmtUsd(h.currentPriceUsd)}</span>
            <span style={s.holdingValue}>{fmtUsd(h.currentValueUsd)}</span>
            <span style={{ ...s.holdingPnl, color: h.unrealizedPnlUsd >= 0 ? '#10b981' : '#ef4444' }}>
              {fmtUsd(h.unrealizedPnlUsd)} ({fmtPct(h.unrealizedPnlPct)})
            </span>
          </button>
          {expandedSymbol === h.symbol && <HoldingDetail h={h} t={t} />}
        </div>
      ))}
    </div>
  );
}

function HoldingDetail({ h, t }) {
  const [status, setStatus] = useState(null);
  const [statusReady, setStatusReady] = useState(false);
  useEffect(() => {
    let active = true;
    setStatusReady(false);
    getPortfolioAssetStatus(h.symbol).then((r) => { if (active) setStatus(r); }).catch(() => {}).finally(() => { if (active) setStatusReady(true); });
    return () => { active = false; };
  }, [h.symbol]);

  // Historical chart data — reuses Technical Analysis' own history endpoint
  // (same data the Technical page charts, keyed by the USDT pair) rather
  // than duplicating a data pipeline just for the portfolio view. Symbols
  // TrustFlow doesn't run daily technical analysis on simply render no charts.
  const [history, setHistory] = useState([]);
  const [historyReady, setHistoryReady] = useState(false);
  useEffect(() => {
    let active = true;
    setHistoryReady(false);
    getTechnicalHistory(`${h.symbol}USDT`, { limit: 90, slim: true })
      .then((r) => { if (active) setHistory(r.docs || []); })
      .catch(() => { if (active) setHistory([]); })
      .finally(() => { if (active) setHistoryReady(true); });
    return () => { active = false; };
  }, [h.symbol]);

  const zonePriceRows = history.filter((d) => d.zoneScore?.green != null && d.price != null)
    .map((d) => ({ date: d.date, green: d.zoneScore.green, red: d.zoneScore.red, price: d.price }));
  const scoreRows = history.filter((d) => d.composite != null).map((d) => ({ date: d.date, composite: d.composite }));
  const volumeRows = history.filter((d) => d.flow?.totalVolume != null).map((d) => ({ date: d.date, volume: d.flow.totalVolume }));

  return (
    <div style={s.holdingBody}>
      <div style={s.detailGrid}>
        <DetailStat label={t('portfolio.avgOpenBuyPrice')} value={fmtUsd(h.avgOpenBuyPriceUsd)} />
        <DetailStat label={t('portfolio.avgBuyPrice')} value={fmtUsd(h.avgBuyPriceUsd)} />
        <DetailStat label={t('portfolio.avgSellPrice')} value={fmtUsd(h.avgSellPriceUsd)} />
        <DetailStat label={t('portfolio.realizedPnl')} value={fmtUsd(h.realizedPnlUsd)} color={h.realizedPnlUsd >= 0 ? '#10b981' : '#ef4444'} />
        <DetailStat label={t('portfolio.totalFees')} value={fmtUsd(h.totalFeesUsd)} />
        <DetailStat label={t('portfolio.totalTax')} value={fmtUsd(h.totalTaxUsd)} />
        <DetailStat label={t('portfolio.marketCap')} value={fmtUsd(h.marketCapUsd)} />
        <DetailStat label={t('portfolio.volume24h')} value={fmtUsd(h.totalVolumeUsd)} />
        <DetailStat label={t('portfolio.priceChange24h')} value={fmtPct(h.priceChange24hPct)} color={h.priceChange24hPct >= 0 ? '#10b981' : '#ef4444'} />
      </div>

      {h.sellRecords?.length > 0 && (
        <div style={s.sellSlippage}>
          <div style={s.detailSectionTitle}>{t('portfolio.slippageTitle')}</div>
          {h.sellRecords.map((sr) => (
            <div key={sr.txId} style={s.slippageRow}>
              <span>{new Date(sr.date).toLocaleDateString('vi-VN')}</span>
              <span>{fmtQty(sr.quantity)} @ {fmtUsd(sr.sellUnitPriceUsd)}</span>
              <span>{t('portfolio.costBasis')}: {fmtUsd(sr.avgCostBasisUnitUsd)}</span>
              <span style={{ color: sr.slippagePct >= 0 ? '#10b981' : '#ef4444' }}>{fmtPct(sr.slippagePct)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={s.detailSectionTitle}>{t('portfolio.statusTitle')}</div>
      {!statusReady ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('portfolio.loading')}</div>
      ) : (
        <div style={s.statusGrid}>
          <StatusChip label={t('portfolio.statusGreenZone')} value={status?.technical ? `${status.technical.zoneGreen ?? '—'}` : null} color="#10b981" empty={t('portfolio.statusNoData')} />
          <StatusChip label={t('portfolio.statusRedZone')} value={status?.technical ? `${status.technical.zoneRed ?? '—'}` : null} color="#ef4444" empty={t('portfolio.statusNoData')} />
          <StatusChip label={t('portfolio.statusComposite')} value={status?.technical ? `${status.technical.composite ?? '—'}/100 (${status.technical.trend})` : null} empty={t('portfolio.statusNoData')} />
          <StatusChip
            label={t('portfolio.statusEtfFlow')}
            value={status?.etfFlow ? `${status.etfFlow.netFlowUsd >= 0 ? '+' : ''}${fmtUsd(status.etfFlow.netFlowUsd)} (${status.etfFlow.date})` : null}
            color={status?.etfFlow?.netFlowUsd >= 0 ? '#10b981' : '#ef4444'}
            empty={t('portfolio.statusNoData')}
          />
          <StatusChip
            label={t('portfolio.statusStaking')}
            value={status?.staking ? `${status.staking.pctOfTotalSupply?.toFixed(2)}% ${t('portfolio.ofTotalSupply')}` : null}
            empty={t('portfolio.statusNoData')}
          />
          {status?.research ? (
            <StatusChip label={t('portfolio.statusResearch')} value={`${status.research.verdict ?? '—'}/5`} />
          ) : (
            <a href="#/research" style={s.researchLink}>{t('portfolio.statusResearchCta')}</a>
          )}
        </div>
      )}

      {historyReady && (zonePriceRows.length > 0 || scoreRows.length > 0 || volumeRows.length > 0) && (
        <>
          {zonePriceRows.length > 0 && (
            <div style={s.chartBlock}>
              <div style={s.detailSectionTitle}>{t('portfolio.chartZonePrice')}</div>
              <ZonePriceChart rows={zonePriceRows} />
              <div style={s.chartLegend}>
                <span style={{ color: '#10b981', fontWeight: 700 }}>■ Green</span>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>■ Red</span>
                <span style={{ color: '#334155', fontWeight: 700 }}>— {t('portfolio.chartPriceLegend')}</span>
              </div>
            </div>
          )}
          {scoreRows.length > 0 && (
            <div style={s.chartBlock}>
              <div style={s.detailSectionTitle}>{t('portfolio.chartMarketScore')}</div>
              <SimpleLineChart rows={scoreRows} dataKey="composite" min={0} max={100} color="var(--accent)" />
            </div>
          )}
          {volumeRows.length > 0 && (
            <div style={s.chartBlock}>
              <div style={s.detailSectionTitle}>{t('portfolio.chartVolume')}</div>
              <SimpleBarChart rows={volumeRows} dataKey="volume" color="#64748b" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Green/Red zone split (0-100, left axis) as a boundary line with red filled
// above it and green filled below — same convention as the Technical page's
// ZoneAreaChart — plus the token's own price threaded through as a second
// line on its own right-axis scale, so a reader can see whether price moved
// with or against the zone shift without switching charts.
function ZonePriceChart({ rows, height = 130 }) {
  if (rows.length === 0) return null;
  const W = 640, padL = 30, padR = 34, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const xPos = (i) => padL + (rows.length > 1 ? (i / (rows.length - 1)) * plotW : plotW / 2);
  const yPos = (v) => padT + plotH - (v / 100) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / 5));

  const prices = rows.map((r) => r.price);
  const priceMin = Math.min(...prices), priceMax = Math.max(...prices);
  const priceRange = (priceMax - priceMin) || 1;
  const priceY = (p) => padT + plotH - ((p - priceMin) / priceRange) * plotH;

  const linePts = rows.map((r, i) => [xPos(i), yPos(r.green)]);
  const lineD = linePts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const firstX = linePts[0][0], lastX = linePts[linePts.length - 1][0];
  const bottomY = padT + plotH;
  const redAreaD = `${lineD} L${lastX.toFixed(1)},${padT} L${firstX.toFixed(1)},${padT} Z`;
  const greenAreaD = `${lineD} L${lastX.toFixed(1)},${bottomY} L${firstX.toFixed(1)},${bottomY} Z`;
  const priceD = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xPos(i).toFixed(1)},${priceY(r.price).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[100, 50, 0].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke={v === 50 ? 'var(--text-dim)' : 'var(--border)'} strokeWidth={1} strokeDasharray={v === 50 ? '2,2' : '3,3'} />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{v}</text>
        </g>
      ))}
      <path d={redAreaD} fill="#ef4444" opacity={0.3} stroke="none" />
      <path d={greenAreaD} fill="#10b981" opacity={0.3} stroke="none" />
      <path d={lineD} fill="none" stroke="#334155" strokeWidth={1} strokeOpacity={0.5} />
      <path d={priceD} fill="none" stroke="#334155" strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      {[priceMax, (priceMax + priceMin) / 2, priceMin].map((p, i) => (
        <text key={i} x={W - padR + 5} y={priceY(p) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="start">{fmtUsd(p)}</text>
      ))}
      {rows.map((r, i) => (
        <circle key={r.date} cx={xPos(i)} cy={priceY(r.price)} r={7} fill="transparent" stroke="none">
          <title>{r.date}: Green {r.green} / Red {r.red} · {fmtUsd(r.price)}</title>
        </circle>
      ))}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i)} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

function SimpleLineChart({ rows, dataKey, min, max, color, height = 100 }) {
  if (rows.length === 0) return null;
  const W = 640, padL = 26, padR = 4, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const vals = rows.map((r) => r[dataKey]);
  const minY = min != null ? min : Math.min(...vals);
  const maxY = max != null ? max : Math.max(...vals);
  const rangeY = (maxY - minY) || 1;
  const xPos = (i) => padL + (rows.length > 1 ? (i / (rows.length - 1)) * plotW : plotW / 2);
  const yPos = (v) => padT + plotH - ((v - minY) / rangeY) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / 5));
  const d = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xPos(i).toFixed(1)},${yPos(r[dataKey]).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[maxY, (maxY + minY) / 2, minY].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{v.toFixed(0)}</text>
        </g>
      ))}
      <path d={d} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      {rows.map((r, i) => (
        <circle key={r.date} cx={xPos(i)} cy={yPos(r[dataKey])} r={7} fill="transparent" stroke="none">
          <title>{r.date}: {r[dataKey].toFixed(1)}</title>
        </circle>
      ))}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i)} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

function fmtVolTick(v) {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

function SimpleBarChart({ rows, dataKey, color, height = 100 }) {
  if (rows.length === 0) return null;
  const W = 640, padL = 34, padR = 4, padT = 8, padB = 18;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const maxY = Math.max(1, ...rows.map((r) => r[dataKey]));
  const groupW = plotW / rows.length;
  const barW = Math.max(1, groupW * 0.6);
  const xPos = (i) => padL + i * groupW + (groupW - barW) / 2;
  const yPos = (v) => padT + plotH - (v / maxY) * plotH;
  const baseY = yPos(0);
  const labelEvery = Math.max(1, Math.ceil(rows.length / 5));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[maxY, maxY / 2, 0].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke="var(--border)" strokeWidth={1} strokeDasharray="3,3" />
          <text x={padL - 5} y={yPos(v) + 3} fontSize={9} fill="var(--text-dim)" textAnchor="end">{fmtVolTick(v)}</text>
        </g>
      ))}
      {rows.map((r, i) => (
        <rect key={r.date} x={xPos(i)} y={yPos(r[dataKey])} width={barW} height={Math.max(0, baseY - yPos(r[dataKey]))} fill={color} opacity={0.85}>
          <title>{r.date}: {fmtVolTick(r[dataKey])}</title>
        </rect>
      ))}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i) + barW / 2} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

function DetailStat({ label, value, color }) {
  return (
    <div style={s.detailStat}>
      <span style={s.detailStatLabel}>{label}</span>
      <span style={{ ...s.detailStatValue, ...(color ? { color } : {}) }}>{value}</span>
    </div>
  );
}
function StatusChip({ label, value, color, empty }) {
  return (
    <div style={s.statusChip}>
      <span style={s.statusChipLabel}>{label}</span>
      <span style={{ ...s.statusChipValue, ...(color && value ? { color } : {}) }}>{value ?? empty}</span>
    </div>
  );
}

// ── Transactions ────────────────────────────────────────────────────────
// Deleting a transaction would silently reshuffle FIFO cost-basis matching
// for every later transaction of that symbol — disabled for now until that
// recalculation impact is surfaced to the user before they confirm.
function TransactionsTab({ transactions, onAdd, t }) {
  return (
    <div>
      <button type="button" style={{ ...s.primaryBtn, marginBottom: 12 }} onClick={onAdd}>+ {t('portfolio.addTransaction')}</button>
      {transactions.length === 0 ? (
        <div style={s.empty}><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('portfolio.noTransactions')}</div></div>
      ) : (
        <div style={s.txTable}>
          {transactions.map((tx) => (
            <div key={tx._id} style={s.txRow}>
              <span style={{ ...s.txSide, color: tx.side === 'buy' ? '#10b981' : '#ef4444' }}>{tx.side === 'buy' ? t('portfolio.buy') : t('portfolio.sell')}</span>
              <span style={s.txSymbol}>{tx.symbol}</span>
              <span>{fmtQty(tx.quantity)}</span>
              <span>{tx.price.toLocaleString('en-US', { maximumFractionDigits: 8 })} {tx.currency}</span>
              <span>{new Date(tx.date).toLocaleDateString('vi-VN')}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {tx.storageLocationId?.exchangeName || tx.storageLocationId?.walletAppName || '—'}
                {tx.network ? ` · ${tx.network}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Cashflow ────────────────────────────────────────────────────────────
function CashflowTab({ cashflows, onAdd, onDelete, t }) {
  return (
    <div>
      <button type="button" style={{ ...s.primaryBtn, marginBottom: 12 }} onClick={onAdd}>+ {t('portfolio.addCashflow')}</button>
      {cashflows.length === 0 ? (
        <div style={s.empty}><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('portfolio.noCashflows')}</div></div>
      ) : (
        <div style={s.txTable}>
          {cashflows.map((cf) => (
            <div key={cf._id} style={s.txRow}>
              <span style={{ ...s.txSide, color: cf.type === 'deposit' ? '#10b981' : '#ef4444' }}>{cf.type === 'deposit' ? t('portfolio.deposit') : t('portfolio.withdraw')}</span>
              <span>{cf.amount.toLocaleString('en-US')} {cf.currency}</span>
              <span>{new Date(cf.date).toLocaleDateString('vi-VN')}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cf.notes}</span>
              <button type="button" style={s.deleteBtn} onClick={() => onDelete(cf._id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Locations ───────────────────────────────────────────────────────────
function LocationsTab({ locations, onAdd, onDelete, t }) {
  return (
    <div>
      <button type="button" style={{ ...s.primaryBtn, marginBottom: 12 }} onClick={onAdd}>+ {t('portfolio.addLocation')}</button>
      {locations.length === 0 ? (
        <div style={s.empty}><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('portfolio.noLocations')}</div></div>
      ) : (
        <div style={s.locationGrid}>
          {locations.map((loc) => (
            <div key={loc._id} style={s.locationCard}>
              <span style={s.locationIcon}>{loc.kind === 'cex' ? '🏦' : '👛'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.locationTitle}>{loc.label || loc.exchangeName || loc.walletAppName}</div>
                {loc.kind === 'cex' ? (
                  <div style={s.locationSub}>{loc.exchangeName}</div>
                ) : (
                  <div style={s.locationSub}>{loc.walletAppName} · {loc.walletAddress.slice(0, 6)}…{loc.walletAddress.slice(-4)}</div>
                )}
              </div>
              <button type="button" style={s.deleteBtn} onClick={() => onDelete(loc._id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Workflow guide ──────────────────────────────────────────────────────
// Explains the intended flow: deposit is the entry point every user needs,
// buy/sell are optional (you can just park cash), withdraw is the exit
// point — capped at whatever's actually in the cash balance.
function WorkflowGuide({ t }) {
  const steps = [
    { icon: '💰', label: t('portfolio.stepDeposit'), required: true },
    { icon: '🛒', label: t('portfolio.stepBuy'), required: false },
    { icon: '💱', label: t('portfolio.stepSell'), required: false },
    { icon: '🏧', label: t('portfolio.stepWithdraw'), required: true },
  ];
  return (
    <div style={s.workflowGuide}>
      {steps.map((step, i) => (
        <React.Fragment key={step.label}>
          <div style={s.workflowStep}>
            <span style={s.workflowIcon}>{step.icon}</span>
            <span style={s.workflowLabel}>{step.label}</span>
            {!step.required && <span style={s.workflowOptional}>{t('portfolio.optional')}</span>}
          </div>
          {i < steps.length - 1 && <span style={s.workflowArrow}>→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Timeline (boustrophedon roadmap) ────────────────────────────────────
const EVENT_STYLE = {
  deposit:  { icon: '💰', color: '#10b981' },
  withdraw: { icon: '🏧', color: '#ef4444' },
  buy:      { icon: '🛒', color: '#3b82f6' },
  sell:     { icon: '💱', color: '#8b5cf6' },
};

function TimelineTab({ t, isMobile }) {
  const [events, setEvents] = useState([]);
  const [oldestFrom, setOldestFrom] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [ready, setReady] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let active = true;
    getPortfolioTimeline({}).then((r) => {
      if (!active) return;
      setEvents(r.events || []);
      setOldestFrom(r.from);
      setHasMore(!!r.hasMore);
    }).catch(() => {}).finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, []);

  const loadMore = async () => {
    if (!oldestFrom || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await getPortfolioTimeline({ before: oldestFrom });
      setEvents((prev) => [...(r.events || []), ...prev]);
      setOldestFrom(r.from);
      setHasMore(!!r.hasMore);
    } catch { /* silent */ }
    finally { setLoadingMore(false); }
  };

  if (!ready) return <div style={s.loading}>{t('portfolio.loading')}</div>;
  if (events.length === 0) {
    return (
      <div style={s.empty}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('portfolio.noTimeline')}</div>
      </div>
    );
  }

  // Fluid width: columns are a percentage-based COUNT, capped at however
  // many events actually exist — with only 3 events, a fixed 6-column grid
  // would leave 3 empty slots trailing off to the right instead of the row
  // filling the available width. Node position/size are percentages of the
  // container; only row height stays a fixed pixel value.
  const maxCols = isMobile ? 2 : 6;
  const cols = Math.max(1, Math.min(maxCols, events.length));
  const nodeH = 96;
  const rows = Math.ceil(events.length / cols);
  const height = rows * nodeH;
  const colPct = 100 / cols;

  // Center position for event index i, honoring the snake direction (even
  // rows left→right, odd rows right→left) — used for both node placement
  // and the connecting path so the two always agree. x is a % of width,
  // y is in the same pixel units as `height`.
  const centerFor = (i) => {
    const r = Math.floor(i / cols);
    const cInRow = i % cols;
    const c = r % 2 === 0 ? cInRow : cols - 1 - cInRow;
    return { xPct: c * colPct + colPct / 2, y: r * nodeH + nodeH / 2 };
  };

  // SVG viewBox uses 0-100 width units (one unit = 1%) stretched to fill the
  // container (preserveAspectRatio="none"), and real pixel units for height
  // — so the connecting path always lines up with the percentage-positioned
  // node divs regardless of actual rendered width.
  const pathD = events.map((_, i) => {
    const { xPct, y } = centerFor(i);
    return `${i === 0 ? 'M' : 'L'}${xPct},${y}`;
  }).join(' ');

  return (
    <div>
      {hasMore && (
        <button type="button" style={{ ...s.tabBtn, marginBottom: 12 }} onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? t('portfolio.loading') : `← ${t('portfolio.loadOlder')}`}
        </button>
      )}
      <div style={{ position: 'relative', width: '100%', height }}>
        <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}>
          <path d={pathD} fill="none" stroke="var(--border-mid)" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeDasharray="6,6" />
        </svg>
        {events.map((ev, i) => {
          const { xPct, y } = centerFor(i);
          const style = EVENT_STYLE[ev.kind] || { icon: '•', color: 'var(--text-muted)' };
          const label = ev.kind === 'deposit' ? t('portfolio.deposit')
            : ev.kind === 'withdraw' ? t('portfolio.withdraw')
            : ev.kind === 'buy' ? `${t('portfolio.buy')} ${ev.symbol}`
            : `${t('portfolio.sell')} ${ev.symbol}`;
          return (
            <div
              key={ev.id}
              style={{
                position: 'absolute', left: `calc(${xPct}% - ${colPct / 2}% + 8px)`, top: y - 34,
                width: `calc(${colPct}% - 16px)`,
                background: 'var(--surface)', border: `1.5px solid ${style.color}`, borderRadius: 10,
                padding: '8px 10px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
              title={ev.notes || ''}
            >
                <div style={{ fontSize: 18, lineHeight: 1 }}>{style.icon}</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: style.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {label}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{fmtUsd(ev.amountUsd)}</div>
                <div style={{ fontSize: 9.5, color: 'var(--text-dim)' }}>{new Date(ev.date).toLocaleDateString('vi-VN')}</div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ── Modals ──────────────────────────────────────────────────────────────
function ModalShell({ title, onClose, children }) {
  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHead}>
          <span style={s.modalTitle}>{title}</span>
          <button type="button" style={s.modalClose} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const OTHER_EXCHANGE = 'Sàn khác';

function AddLocationModal({ presets, onClose, onSaved, t }) {
  const [kind, setKind] = useState('cex');
  const [exchangeName, setExchangeName] = useState(presets.cexPresets[0]?.name || '');
  const [customExchangeName, setCustomExchangeName] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletAppName, setWalletAppName] = useState(presets.walletAppPresets[0] || '');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    const finalExchangeName = exchangeName === OTHER_EXCHANGE ? customExchangeName.trim() : exchangeName;
    if (kind === 'cex' && !finalExchangeName) { setErr(t('portfolio.errExchangeName')); return; }
    setSaving(true);
    try {
      await createPortfolioLocation({ kind, exchangeName: finalExchangeName, walletAddress, walletAppName, label });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={t('portfolio.addLocation')} onClose={onClose}>
      <div style={s.formRow}>
        <button type="button" style={kind === 'cex' ? s.tabBtnActive : s.tabBtn} onClick={() => setKind('cex')}>🏦 {t('portfolio.locationCex')}</button>
        <button type="button" style={kind === 'wallet' ? s.tabBtnActive : s.tabBtn} onClick={() => setKind('wallet')}>👛 {t('portfolio.locationWallet')}</button>
      </div>
      {kind === 'cex' ? (
        <div style={s.formField}>
          <label style={s.formLabel}>{t('portfolio.exchangeName')}</label>
          <select style={s.formSelect} value={exchangeName} onChange={(e) => setExchangeName(e.target.value)}>
            {presets.cexPresets.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            <option value={OTHER_EXCHANGE}>{OTHER_EXCHANGE}</option>
          </select>
          {exchangeName === OTHER_EXCHANGE && (
            <input
              style={{ ...s.formInput, marginTop: 6 }} value={customExchangeName}
              onChange={(e) => setCustomExchangeName(e.target.value)} placeholder={t('portfolio.exchangeName')}
            />
          )}
        </div>
      ) : (
        <>
          <div style={s.formField}>
            <label style={s.formLabel}>{t('portfolio.walletAddress')}</label>
            <input style={s.formInput} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} placeholder="0x... / bc1... / ..." />
          </div>
          <div style={s.formField}>
            <label style={s.formLabel}>{t('portfolio.walletAppName')}</label>
            <select style={s.formSelect} value={walletAppName} onChange={(e) => setWalletAppName(e.target.value)}>
              {presets.walletAppPresets.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </>
      )}
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.labelOptional')}</label>
        <input style={s.formInput} value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      {err && <div style={s.formError}>{err}</div>}
      <button type="button" style={s.primaryBtn} disabled={saving} onClick={submit}>{saving ? '...' : t('portfolio.save')}</button>
    </ModalShell>
  );
}

function AddCashflowModal({ cashBalanceUsd, onClose, onSaved, t }) {
  const [type, setType] = useState('deposit');
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayInput());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fxRate, setFxRate] = useState(null);

  useEffect(() => { getPortfolioFxRate().then((r) => setFxRate(r.rate)).catch(() => {}); }, []);

  const amountUsd = currency === 'VND' ? (fxRate ? parseFloat(amount || 0) / fxRate : null) : parseFloat(amount || 0);
  const exceedsCash = type === 'withdraw' && amountUsd != null && amountUsd > cashBalanceUsd;

  const submit = async () => {
    setErr('');
    if (!(parseFloat(amount) > 0)) { setErr(t('portfolio.errAmount')); return; }
    if (exceedsCash) { setErr(t('portfolio.errExceedsCash', { cash: fmtUsd(cashBalanceUsd) })); return; }
    setSaving(true);
    try {
      await createPortfolioCashflow({ type, currency, amount: parseFloat(amount), date, notes });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={t('portfolio.addCashflow')} onClose={onClose}>
      <div style={s.formRow}>
        <button type="button" style={type === 'deposit' ? s.tabBtnActive : s.tabBtn} onClick={() => setType('deposit')}>{t('portfolio.deposit')}</button>
        <button type="button" style={type === 'withdraw' ? s.tabBtnActive : s.tabBtn} onClick={() => setType('withdraw')}>{t('portfolio.withdraw')}</button>
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.amount')}</label>
        <div style={s.formRow}>
          <input style={s.formInput} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <select style={s.formSelect} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="USD">USD</option>
            <option value="VND">VND</option>
          </select>
        </div>
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.date')}</label>
        <input style={s.formInput} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.notesOptional')}</label>
        <input style={s.formInput} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {type === 'withdraw' && (
        <div style={{ fontSize: 11, color: exceedsCash ? '#ef4444' : 'var(--text-muted)' }}>
          {t('portfolio.availableCash', { cash: fmtUsd(cashBalanceUsd) })}
        </div>
      )}
      {err && <div style={s.formError}>{err}</div>}
      <button type="button" style={s.primaryBtn} disabled={saving || exceedsCash} onClick={submit}>{saving ? '...' : t('portfolio.save')}</button>
    </ModalShell>
  );
}

function AddTransactionModal({ locations, presets, holdings, cashBalanceUsd, onClose, onSaved, t }) {
  const [side, setSide] = useState('buy');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [storageLocationId, setStorageLocationId] = useState('');
  const [network, setNetwork] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [feePct, setFeePct] = useState('0.1');
  const [date, setDate] = useState(todayInput());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fxRate, setFxRate] = useState(null);

  useEffect(() => {
    if (query.trim().length < 2 || selected) { setResults([]); return undefined; }
    let active = true;
    const timer = setTimeout(() => {
      searchPortfolioAsset(query).then((r) => { if (active) setResults(r.results || []); }).catch(() => {});
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query, selected]);

  useEffect(() => { getPortfolioFxRate().then((r) => setFxRate(r.rate)).catch(() => {}); }, []);

  const selectedLocation = locations.find((l) => l._id === storageLocationId) || null;
  const isWallet = selectedLocation?.kind === 'wallet';
  useEffect(() => { if (!isWallet) setNetwork(''); }, [isWallet]);

  const amountLocal = (parseFloat(price) || 0) * (parseFloat(quantity) || 0);
  const feeLocal = amountLocal * ((parseFloat(feePct) || 0) / 100);
  const taxLocal = side === 'sell' ? amountLocal * 0.001 : 0;
  const toUsd = (v) => (currency === 'VND' ? (fxRate ? v / fxRate : null) : v);

  // Held quantity for the selected asset — sells can never exceed this (the
  // backend enforces it authoritatively via FIFO replay; this is just an
  // early, friendlier warning before the round-trip).
  const heldQty = selected ? (holdings.find((h) => h.symbol === selected.symbol)?.openQuantity ?? 0) : null;
  const exceedsHeld = side === 'sell' && selected && parseFloat(quantity) > heldQty;

  // A buy can't spend more than the current cash balance — same rule the
  // backend enforces authoritatively; this is just an early warning.
  const buyOutlayUsd = toUsd(amountLocal + feeLocal);
  const exceedsCash = side === 'buy' && buyOutlayUsd != null && buyOutlayUsd > cashBalanceUsd;

  const submit = async () => {
    setErr('');
    if (!selected) { setErr(t('portfolio.errAsset')); return; }
    if (!(parseFloat(price) > 0) || !(parseFloat(quantity) > 0)) { setErr(t('portfolio.errAmount')); return; }
    if (side === 'sell' && !(heldQty > 0)) { setErr(t('portfolio.errNoHoldings', { symbol: selected.symbol })); return; }
    if (exceedsHeld) { setErr(t('portfolio.errExceedsHeld', { qty: fmtQty(heldQty), symbol: selected.symbol })); return; }
    if (exceedsCash) { setErr(t('portfolio.errExceedsCash', { cash: fmtUsd(cashBalanceUsd) })); return; }
    setSaving(true);
    try {
      await createPortfolioTransaction({
        symbol: selected.symbol, coingeckoId: selected.coingeckoId,
        storageLocationId: storageLocationId || null, network: isWallet ? network : null, side, currency,
        price: parseFloat(price), quantity: parseFloat(quantity), feePct: parseFloat(feePct) || 0,
        date, notes,
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={t('portfolio.addTransaction')} onClose={onClose}>
      <div style={s.formRow}>
        <button type="button" style={side === 'buy' ? s.tabBtnActive : s.tabBtn} onClick={() => setSide('buy')}>{t('portfolio.buy')}</button>
        <button type="button" style={side === 'sell' ? s.tabBtnActive : s.tabBtn} onClick={() => setSide('sell')}>{t('portfolio.sell')}</button>
      </div>

      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.asset')}</label>
        {selected ? (
          <div style={s.selectedAsset}>
            <span>{selected.symbol} — {selected.name}</span>
            <button type="button" style={s.deleteBtn} onClick={() => { setSelected(null); setQuery(''); }}>✕</button>
          </div>
        ) : (
          <>
            <input style={s.formInput} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('portfolio.searchAssetPlaceholder')} />
            {results.length > 0 && (
              <div style={s.searchResults}>
                {results.map((r) => (
                  <button key={r.coingeckoId} type="button" style={s.searchResultItem} onClick={() => { setSelected(r); setResults([]); }}>
                    {r.symbol} — {r.name}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.storageLocation')}</label>
        <select style={s.formSelect} value={storageLocationId} onChange={(e) => setStorageLocationId(e.target.value)}>
          <option value="">—</option>
          {locations.map((loc) => (
            <option key={loc._id} value={loc._id}>{loc.label || loc.exchangeName || loc.walletAppName}</option>
          ))}
        </select>
      </div>

      {isWallet && (
        <div style={s.formField}>
          <label style={s.formLabel}>{t('portfolio.network')}</label>
          <select style={s.formSelect} value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="">—</option>
            {presets.walletNetworkPresets.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}

      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.price')}</label>
        <div style={s.formRow}>
          <input style={s.formInput} type="number" value={price} onChange={(e) => setPrice(e.target.value)} />
          <select style={s.formSelect} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="USD">USD</option>
            <option value="VND">VND</option>
          </select>
        </div>
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.quantity')}</label>
        <input style={s.formInput} type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        {side === 'sell' && selected && (
          <span style={{ fontSize: 11, color: exceedsHeld ? '#ef4444' : 'var(--text-muted)' }}>
            {t('portfolio.availableToSell', { qty: fmtQty(heldQty), symbol: selected.symbol })}
          </span>
        )}
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.feePct')}</label>
        <input style={s.formInput} type="number" value={feePct} onChange={(e) => setFeePct(e.target.value)} />
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.date')}</label>
        <input style={s.formInput} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div style={s.formField}>
        <label style={s.formLabel}>{t('portfolio.notesOptional')}</label>
        <input style={s.formInput} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {amountLocal > 0 && (
        <div style={s.calcPreview}>
          <span>{t('portfolio.previewAmount')}: {amountLocal.toLocaleString('en-US')} {currency}</span>
          <span>{t('portfolio.previewFee')}: {feeLocal.toLocaleString('en-US')} {currency}</span>
          {side === 'sell' && <span>{t('portfolio.previewTax')}: {taxLocal.toLocaleString('en-US')} {currency} (0.1%)</span>}
          {side === 'buy' && (
            <span style={exceedsCash ? { color: '#ef4444' } : undefined}>
              {t('portfolio.availableCash', { cash: fmtUsd(cashBalanceUsd) })}
            </span>
          )}
        </div>
      )}

      {err && <div style={s.formError}>{err}</div>}
      <button type="button" style={s.primaryBtn} disabled={saving || exceedsHeld || exceedsCash} onClick={submit}>{saving ? '...' : t('portfolio.save')}</button>
    </ModalShell>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  notice: {
    fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
    marginBottom: 16, lineHeight: 1.6,
  },

  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '50px 20px', gap: 12 },

  workflowGuide: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
    padding: '12px 16px', marginBottom: 16,
  },
  workflowStep: { display: 'flex', alignItems: 'center', gap: 6 },
  workflowIcon: { fontSize: 16 },
  workflowLabel: { fontSize: 12.5, fontWeight: 700, color: 'var(--text)' },
  workflowOptional: { fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' },
  workflowArrow: { color: 'var(--text-dim)', fontSize: 14 },

  cards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  card: { flex: '1 1 170px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontSize: 17, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },

  tabBar: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', paddingBottom: 10 },
  tabBtn: {
    fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
  },
  tabBtnActive: {
    fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'var(--accent)',
    border: '1px solid var(--accent)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
  },

  primaryBtn: {
    fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--accent)',
    border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', alignSelf: 'flex-start',
  },
  deleteBtn: {
    fontSize: 12, color: 'var(--text-dim)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
  },

  holdingsList: { display: 'flex', flexDirection: 'column', gap: 8 },
  holdingCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  holdingHead: {
    display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1.4fr', gap: 10, alignItems: 'center',
    width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '12px 16px', cursor: 'pointer', font: 'inherit',
  },
  holdingSymbol: { fontWeight: 800, color: 'var(--text)', fontSize: 14 },
  holdingQty: { fontSize: 12.5, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' },
  holdingPrice: { fontSize: 12.5, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' },
  holdingValue: { fontSize: 13, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },
  holdingPnl: { fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  holdingBody: { borderTop: '1px solid var(--border)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 },

  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 },
  detailStat: { display: 'flex', flexDirection: 'column', gap: 2 },
  detailStatLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 },
  detailStatValue: { fontSize: 13, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },
  detailSectionTitle: { fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },

  sellSlippage: { display: 'flex', flexDirection: 'column', gap: 6 },
  slippageRow: { display: 'flex', gap: 12, fontSize: 11.5, color: 'var(--text-dim)', flexWrap: 'wrap' },

  statusGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 },
  statusChip: {
    display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '8px 10px',
  },
  statusChipLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 },
  statusChipValue: { fontSize: 12.5, fontWeight: 700, color: 'var(--text)' },
  researchLink: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
    color: 'var(--accent)', background: 'var(--bg)', border: '1px dashed var(--accent)', borderRadius: 8, padding: '8px 10px', textDecoration: 'none',
  },

  chartBlock: { display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' },
  chartLegend: { display: 'flex', gap: 12, fontSize: 10.5, flexWrap: 'wrap' },

  txTable: { display: 'flex', flexDirection: 'column', gap: 6 },
  txRow: {
    display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: 'var(--text-dim)', flexWrap: 'wrap',
  },
  txSide: { fontWeight: 800, minWidth: 40 },
  txSymbol: { fontWeight: 700, color: 'var(--text)' },

  locationGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  locationCard: {
    display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '10px 14px',
  },
  locationIcon: { fontSize: 20 },
  locationTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)' },
  locationSub: { fontSize: 11.5, color: 'var(--text-muted)' },

  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
  },
  modalBox: {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 20,
    maxWidth: 440, width: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12,
  },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  modalClose: { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer' },

  formRow: { display: 'flex', gap: 8 },
  formField: { display: 'flex', flexDirection: 'column', gap: 4 },
  formLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' },
  formInput: {
    flex: 1, background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 10px', fontSize: 13, outline: 'none', minWidth: 0,
  },
  formSelect: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 10px', fontSize: 13, cursor: 'pointer',
  },
  formError: { fontSize: 12, color: '#ef4444' },

  selectedAsset: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: 'var(--text)',
  },
  searchResults: {
    display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 8, maxHeight: 180, overflowY: 'auto',
  },
  searchResultItem: {
    textAlign: 'left', background: 'transparent', border: 'none', padding: '7px 10px', fontSize: 12.5,
    color: 'var(--text)', cursor: 'pointer',
  },
  calcPreview: {
    display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, color: 'var(--text-muted)',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px',
  },
};
